import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Box, Button, Group, Stack, Table, Tag, Text, TextInput, Title } from '@grovus/design-system'
import { DICTIONARY_COPY, type DictionaryCopy, type Lang } from '../i18n'
import { fill } from '../creatives/format'
import {
  failureLine,
  listValues,
  valueLabel,
  type DictionaryOp,
  type HypothesisPatch,
  type HypothesisValue,
  type WriteOutcome,
} from './dictionaryApi'

const BORDER = '1px solid var(--mantine-color-default-border)'

interface Props {
  lang: Lang
  /** The company the screen was mounted for. The shell keys the screen by it, so it never changes. */
  companyId: string
  /** Owner, Admin and Member. A Viewer reads the table with no actions. */
  canEdit: boolean
  /**
   * The shell issues every write, so its completion raises the picker's and the
   * reporting's revisions even if this screen has unmounted by then.
   */
  onWrite: (companyId: string, op: DictionaryOp) => Promise<WriteOutcome<HypothesisValue>>
  /**
   * Raised by the shell after every committed dictionary write and assignment in this
   * company, so a write that lands after this screen mounted, or after an earlier screen
   * started it, still reaches the table and its counts.
   */
  revision?: number
  /** A `404` on the list: the shell re-checks whether the caller still belongs. */
  onAccessLost: (companyId: string) => void
  onBack: () => void
  /** Injected in tests; the real screen uses the global. */
  fetchImpl?: typeof fetch
}

type ListState =
  | { phase: 'loading' }
  | { phase: 'ready'; values: HypothesisValue[] }
  | { phase: 'failed' }

/** The open add or edit dialog, with its draft. */
type Dialog = { mode: 'add' } | { mode: 'edit'; value: HypothesisValue }

interface Draft {
  /** The one name a new value is created with (D13); the edit form uses the labels. */
  name: string
  labelEn: string
  labelRu: string
  labelUk: string
  claim: string
}

const EMPTY_DRAFT: Draft = { name: '', labelEn: '', labelRu: '', labelUk: '', claim: '' }

/** Only the fields the edit changed, so an unchanged label never reads as a relabel. */
function patchOf(value: HypothesisValue, draft: Draft): HypothesisPatch {
  const patch: HypothesisPatch = {}
  const uk = draft.labelUk.trim() === '' ? null : draft.labelUk.trim()
  const claim = draft.claim.trim() === '' ? null : draft.claim.trim()
  if (draft.labelEn.trim() !== value.labelEn) patch.labelEn = draft.labelEn.trim()
  if (draft.labelRu.trim() !== value.labelRu) patch.labelRu = draft.labelRu.trim()
  if (uk !== value.labelUk) patch.labelUk = uk
  if (claim !== value.claim) patch.claim = claim
  return patch
}

/**
 * The hypothesis dictionary (GRO-157): the canvas Naming > Dictionaries tab, reduced to
 * the one dictionary this slice manages. Values are added and edited in one dialog, and
 * archived or restored from their row; there is no delete. Seeded values belong to the
 * demo data and take no action. Every write records the company it was submitted for,
 * and the shell keys the screen by company, so a completion that lands after a switch
 * changes nothing here.
 */
export function DictionaryScreen({ lang, companyId, canEdit, onWrite, onAccessLost, onBack, revision = 0, fetchImpl }: Props) {
  const copy = DICTIONARY_COPY[lang]
  const [list, setList] = useState<ListState>({ phase: 'loading' })
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [dialogLine, setDialogLine] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // The row whose archive or restore is out, so its button cannot be pressed twice.
  const [rowBusy, setRowBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ role: 'status' | 'alert'; text: string } | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const opener = useRef<HTMLElement | null>(null)

  // `alive` and the company a completion is compared with: the key already unmounts the
  // screen on a switch, and this holds the same line for a caller that does not key it.
  const live = useRef({ alive: true, companyId })
  live.current.companyId = companyId
  useEffect(() => {
    live.current.alive = true
    return () => { live.current.alive = false }
  }, [])
  const answers = (submittedFor: string) => live.current.alive && live.current.companyId === submittedFor

  // Only the latest read lands: an earlier one may answer from before the write that
  // moved the revision. A reload over a table keeps the table until its answer arrives.
  const reads = useRef(0)
  const load = useCallback(async () => {
    const read = ++reads.current
    setList((prev) => (prev.phase === 'ready' ? prev : { phase: 'loading' }))
    const outcome = await listValues(companyId, 'idea', fetchImpl)
    if (!answers(companyId) || read !== reads.current) return
    if (outcome.status === 'stale' || outcome.status === 'expired') return
    if (outcome.status === 'notFound') onAccessLost(companyId)
    setList(outcome.status === 'ok' ? { phase: 'ready', values: outcome.body } : { phase: 'failed' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, fetchImpl, onAccessLost])
  useEffect(() => { void load() }, [load, revision])

  const replace = (value: HypothesisValue) =>
    setList((prev) => {
      if (prev.phase !== 'ready') return prev
      const at = prev.values.findIndex((candidate) => candidate.id === value.id)
      return {
        phase: 'ready',
        values: at === -1 ? [...prev.values, value] : prev.values.map((candidate) => (candidate.id === value.id ? value : candidate)),
      }
    })

  function open(next: Dialog) {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setDialog(next)
    setDialogLine(null)
    setDraft(next.mode === 'add' ? EMPTY_DRAFT : {
      name: '',
      labelEn: next.value.labelEn,
      labelRu: next.value.labelRu,
      labelUk: next.value.labelUk ?? '',
      claim: next.value.claim ?? '',
    })
  }

  function close() {
    setDialog(null)
    setDialogLine(null)
    opener.current?.focus()
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (dialog === null || saving) return
    const missing = dialog.mode === 'add'
      ? draft.name.trim() === ''
      : draft.labelEn.trim() === '' || draft.labelRu.trim() === ''
    if (missing) {
      setDialogLine(dialog.mode === 'add' ? copy.nameRequired : copy.required)
      return
    }
    let op: DictionaryOp
    if (dialog.mode === 'add') {
      op = {
        kind: 'add',
        axis: 'idea',
        draft: { name: draft.name.trim(), claim: draft.claim.trim() === '' ? null : draft.claim.trim() },
      }
    } else {
      const patch = patchOf(dialog.value, draft)
      if (Object.keys(patch).length === 0) {
        close()
        return
      }
      op = { kind: 'update', id: dialog.value.id, patch }
    }
    const submittedFor = companyId
    setSaving(true)
    setDialogLine(null)
    const outcome = await onWrite(submittedFor, op)
    if (!answers(submittedFor)) return
    setSaving(false)
    if (outcome.status === 'ok') {
      replace(outcome.body)
      setNotice({ role: 'status', text: op.kind === 'add' ? copy.added : copy.saved })
      close()
      return
    }
    // The create form has one name, so a refused empty label names that field.
    const refusedEmpty = op.kind === 'add' && outcome.status === 'invalid' && outcome.code === 'empty_label'
    setDialogLine(refusedEmpty ? copy.nameRequired : failureLine(outcome, copy))
  }

  async function setArchived(value: HypothesisValue, archived: boolean) {
    const submittedFor = companyId
    setRowBusy(value.id)
    setNotice(null)
    const outcome = await onWrite(submittedFor, { kind: 'update', id: value.id, patch: { archived } })
    if (!answers(submittedFor)) return
    setRowBusy(null)
    if (outcome.status === 'ok') {
      replace(outcome.body)
      setNotice({ role: 'status', text: archived ? copy.archivedDone : copy.restoredDone })
      return
    }
    const line = failureLine(outcome, copy)
    if (line !== null) setNotice({ role: 'alert', text: line })
  }

  const values = list.phase === 'ready' ? list.values : []
  const current = values.filter((value) => !value.archived)
  const archived = values.filter((value) => value.archived)

  function table(rows: HypothesisValue[], testId: string) {
    return (
      <Box style={{ border: BORDER, borderRadius: 'var(--mantine-radius-md)', overflowX: 'auto' }}>
        <Table data-testid={testId}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th><span className="grv-label">{copy.colLabel}</span></Table.Th>
              <Table.Th><span className="grv-label">{copy.colClaim}</span></Table.Th>
              <Table.Th style={{ textAlign: 'right' }}><span className="grv-label">{copy.colCreatives}</span></Table.Th>
              <Table.Th><span className="grv-label">{copy.colStatus}</span></Table.Th>
              {canEdit && <Table.Th><span className="grv-visually-hidden">{copy.colActions}</span></Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((value) => (
              <Table.Tr key={value.id} data-testid={`dictionary-row-${value.id}`}>
                <Table.Td>{valueLabel(value, lang)}</Table.Td>
                <Table.Td>
                  <Text size="sm" className={value.claim === null ? 'grv-muted' : undefined} style={{ maxWidth: 420 }}>
                    {value.claim ?? '-'}
                  </Text>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{value.creatives}</Table.Td>
                <Table.Td>
                  {value.demo ? (
                    <Tag tt="none" size="sm" data-testid="sample-mark">{copy.sample}</Tag>
                  ) : (
                    <Text size="sm" component="span">{value.archived ? copy.archived : copy.active}</Text>
                  )}
                </Table.Td>
                {canEdit && (
                  <Table.Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {!value.demo && (
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        {!value.archived && (
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            disabled={rowBusy !== null}
                            onClick={() => open({ mode: 'edit', value })}
                            aria-label={`${copy.edit}: ${valueLabel(value, lang)}`}
                          >
                            {copy.edit}
                          </Button>
                        )}
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          loading={rowBusy === value.id}
                          disabled={rowBusy !== null && rowBusy !== value.id}
                          onClick={() => { void setArchived(value, !value.archived) }}
                          aria-label={`${value.archived ? copy.restore : copy.archive}: ${valueLabel(value, lang)}`}
                        >
                          {value.archived ? copy.restore : copy.archive}
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>
    )
  }

  return (
    <Stack gap={16} data-testid="dictionary-screen">
      <Group>
        <Button variant="subtle" size="compact-sm" onClick={onBack} data-testid="dictionary-back">{copy.back}</Button>
      </Group>
      <Group justify="space-between" align="baseline" wrap="wrap" gap="xs">
        <Group align="baseline" gap="sm">
          <Title order={2} fz="lg">{copy.title}</Title>
          {list.phase === 'ready' && (
            <Text size="xs" className="grv-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fill(copy.count, { n: values.length })}
            </Text>
          )}
        </Group>
        {canEdit && (
          <Button variant="default" size="compact-sm" onClick={() => open({ mode: 'add' })} disabled={list.phase !== 'ready'}>
            {copy.add}
          </Button>
        )}
      </Group>
      <Text size="sm" style={{ maxWidth: 660 }}>{copy.lede}</Text>

      {notice !== null && <Text size="sm" role={notice.role} data-testid="dictionary-notice">{notice.text}</Text>}

      {list.phase === 'loading' && <Text size="sm" className="grv-muted">{copy.loading}</Text>}
      {list.phase === 'failed' && (
        <Group gap="xs" role="alert">
          <Text size="sm">{copy.loadFailed}</Text>
          <Button size="compact-sm" variant="default" onClick={() => { void load() }}>{copy.retry}</Button>
        </Group>
      )}
      {list.phase === 'ready' && current.length === 0 && archived.length === 0 && (
        <Text size="sm" className="grv-muted">{copy.empty}</Text>
      )}
      {current.length > 0 && table(current, 'dictionary-table')}

      {archived.length > 0 && (
        <Box>
          <Button
            variant="subtle"
            size="compact-sm"
            aria-expanded={showArchived}
            onClick={() => setShowArchived((open) => !open)}
            data-testid="dictionary-archived-toggle"
          >
            {fill(copy.archivedSection, { n: archived.length })}
          </Button>
          {showArchived && <Box pt={8}>{table(archived, 'dictionary-archived')}</Box>}
        </Box>
      )}

      {!canEdit && <Text size="xs" className="grv-muted" style={{ maxWidth: 620 }}>{copy.viewerNote}</Text>}

      {dialog !== null && (
        <ValueDialog
          copy={copy}
          title={dialog.mode === 'add' ? copy.addTitle : copy.editTitle}
          adding={dialog.mode === 'add'}
          draft={draft}
          onDraft={setDraft}
          line={dialogLine}
          saving={saving}
          onSubmit={(event) => { void submit(event) }}
          onCancel={close}
        />
      )}
    </Stack>
  )
}

interface DialogProps {
  copy: DictionaryCopy
  title: string
  /** The create form asks for one name; the edit form keeps the three labels. */
  adding: boolean
  draft: Draft
  onDraft: (draft: Draft) => void
  line: string | null
  saving: boolean
  onSubmit: (event: FormEvent) => void
  onCancel: () => void
}

/**
 * The add and edit dialog. The design system exports no modal, so it is drawn here: a
 * backdrop over the page, focus taken to the first field and held inside while open, and
 * Escape or Cancel to close. Above the header band (z-index 80) and its disclosures (81),
 * because the page under it is inert while the dialog is open.
 */
function ValueDialog({ copy, title, adding, draft, onDraft, line, saving, onSubmit, onCancel }: DialogProps) {
  const titleId = useId()
  const panel = useRef<HTMLDivElement>(null)

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      if (!saving) onCancel()
      return
    }
    if (event.key !== 'Tab' || panel.current === null) return
    const focusable = [...panel.current.querySelectorAll<HTMLElement>('input, textarea, button')].filter((element) => !element.hasAttribute('disabled'))
    if (focusable.length === 0) return
    const [head, tail] = [focusable[0], focusable[focusable.length - 1]]
    if (event.shiftKey && document.activeElement === head) {
      event.preventDefault()
      tail.focus()
    } else if (!event.shiftKey && document.activeElement === tail) {
      event.preventDefault()
      head.focus()
    }
  }

  const field = (key: keyof Draft) => ({
    value: draft[key],
    onChange: (event: { currentTarget: { value: string } }) => onDraft({ ...draft, [key]: event.currentTarget.value }),
    autoComplete: 'off',
  })

  return (
    <Box
      style={{
        position: 'fixed', inset: 0, zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0, 0, 0, 0.45)',
      }}
    >
      <Box
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        data-testid="dictionary-dialog"
        p={20}
        style={{
          width: 480, maxWidth: '100%', maxHeight: '100%', overflowY: 'auto',
          background: 'var(--mantine-color-body)', border: BORDER, borderRadius: 'var(--mantine-radius-lg)',
        }}
      >
        <form onSubmit={onSubmit} noValidate>
          <Stack gap={12}>
            <Title order={3} fz="md" id={titleId}>{title}</Title>
            {adding ? (
              <TextInput autoFocus label={copy.name} required {...field('name')} />
            ) : (
              <>
                <TextInput autoFocus label={copy.labelEn} required {...field('labelEn')} />
                <TextInput label={copy.labelRu} required {...field('labelRu')} />
                <TextInput label={copy.labelUk} description={copy.ukHint} {...field('labelUk')} />
              </>
            )}
            <TextInput label={copy.claim} description={copy.claimHint} {...field('claim')} />
            {line !== null && <Text size="sm" role="alert">{line}</Text>}
            <Group justify="flex-end" gap="xs">
              <Button variant="default" onClick={onCancel} disabled={saving}>{copy.cancel}</Button>
              <Button type="submit" loading={saving}>{copy.save}</Button>
            </Group>
          </Stack>
        </form>
      </Box>
    </Box>
  )
}
